import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { email } from '@vuelidate/validators';

export const MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const INPUT_TYPES = {
  EMAIL: 'email',
  TEL: 'tel',
  TEXT: 'text',
};

// Converte um número possivelmente SEM "+" em E.164, assumindo Brasil (+55)
// quando falta o código do país. Espelha o normalizePhoneNumber do fluxo de
// nova conversa, pra o agente poder digitar "55991577388" em vez de "+55...".
export const guessE164 = value => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (String(value).trim().startsWith('+')) return `+${digits}`;
  if (digits.length >= 12) return `+${digits}`;
  return `+55${digits}`;
};

export const validatePhoneNumber = value => {
  if (!value) return true;
  try {
    if (isValidPhoneNumber(value)) return true;
    const e164 = guessE164(value);
    return e164 ? isValidPhoneNumber(e164) : false;
  } catch (error) {
    return false;
  }
};

const tryParse = candidate => {
  try {
    return parsePhoneNumber(candidate);
  } catch (error) {
    return null;
  }
};

export const formatPhoneNumber = value => {
  // parsePhoneNumber lança exceção para número sem "+", então cada tentativa
  // é isolada — senão o fallback BR nunca seria alcançado.
  let phoneNumber = tryParse(value);
  if (!phoneNumber?.isValid()) {
    const e164 = guessE164(value);
    const brNumber = e164 ? tryParse(e164) : null;
    if (brNumber?.isValid()) phoneNumber = brNumber;
  }
  return {
    isValid: phoneNumber?.isValid() || false,
    formattedValue: phoneNumber?.formatInternational() || value,
  };
};

export const getValidationRules = type => ({
  newTag: {
    ...(type === INPUT_TYPES.EMAIL ? { email } : {}),
    ...(type === INPUT_TYPES.TEL ? { isValidPhone: validatePhoneNumber } : {}),
  },
});

export const checkTagTypeValidity = (type, value, v$) => {
  if (type === INPUT_TYPES.TEL) {
    return !validatePhoneNumber(value);
  }
  return v$.$invalid;
};

export const validateAndFormatNewTag = (
  trimmedNewTag,
  type,
  isNewTagInValidType
) => {
  let isValid = true;
  let formattedValue = trimmedNewTag;

  if (type === INPUT_TYPES.EMAIL) {
    isValid = !isNewTagInValidType;
  } else if (type === INPUT_TYPES.TEL) {
    const { isValid: phoneValid, formattedValue: phoneFormatted } =
      formatPhoneNumber(trimmedNewTag);
    isValid = phoneValid;
    formattedValue = phoneFormatted;
  }

  return { isValid, formattedValue };
};

export const createNewTagMenuItem = (formattedValue, trimmedNewTag, type) => ({
  label:
    type === INPUT_TYPES.TEL
      ? `Criar contato: ${formattedValue}`
      : `Criar contato: ${formattedValue}`,
  value: trimmedNewTag,
  ...(type === INPUT_TYPES.EMAIL ? { email: trimmedNewTag } : {}),
  ...(type === INPUT_TYPES.TEL ? { phoneNumber: trimmedNewTag } : {}),
  icon: 'i-lucide-user-plus',
  action: 'create',
});

export const buildTagMenuItems = ({
  mode,
  tags,
  menuItems,
  newTag,
  isLoading,
  type,
  isNewTagInValidType,
}) => {
  if (mode === MODE.SINGLE && tags.length >= 1) return [];

  const availableMenuItems = menuItems.filter(
    item => !tags.includes(item.label)
  );

  // Show typed value as suggestion only if:
  // 1. There's a value being typed
  // 2. The value isn't already in the tags
  // 3. Validation passes (email/phone) and There are no menu items available
  const trimmedNewTag = newTag?.trim();
  const shouldShowTypedValue =
    trimmedNewTag &&
    !tags.includes(trimmedNewTag) &&
    !isLoading &&
    !availableMenuItems.length;

  if (shouldShowTypedValue) {
    const { isValid, formattedValue } = validateAndFormatNewTag(
      trimmedNewTag,
      type,
      isNewTagInValidType
    );

    if (isValid) {
      return [createNewTagMenuItem(formattedValue, trimmedNewTag, type)];
    }
  }

  return availableMenuItems;
};

export const canAddTag = (mode, tagsLength) =>
  !(mode === MODE.SINGLE && tagsLength >= 1);

export const findMatchingMenuItem = (menuItems, value) =>
  menuItems.find(item => item.email === value);
